import { Component } from '@theme/component';
import { isClickedOutside, isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

export class ProductHotspotComponent extends Component {
  requiredRefs = ['trigger', 'dialog'];
  #pointerenterHandler = null;
  timer = null;
  #isMeasuring = false;
  #openedAt = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#handleBreakpointChange();
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeDesktopListeners();
    mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
  }

  #setupDesktopListeners() {
    const { trigger, dialog } = this.refs;
    const pointerenterHandler = () => {
      if (dialog.open) return;
      this.timer = setTimeout(() => {
        this.showDialog();
      }, 120);
      trigger.addEventListener('pointerleave', this.#handlePointerLeave);
    };
    this.#pointerenterHandler = pointerenterHandler;
    trigger.addEventListener('pointerenter', pointerenterHandler);
  }

  #removeDesktopListeners() {
    const { trigger } = this.refs;
    if (this.#pointerenterHandler) {
      trigger.removeEventListener('pointerenter', this.#pointerenterHandler);
      trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
      this.#pointerenterHandler = null;
    }
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  #handleBreakpointChange = () => {
    this.#removeDesktopListeners();
    if (!isMobileBreakpoint()) {
      this.#setupDesktopListeners();
    }
  };

  #calculateDialogPlacement() {
    const { trigger, dialog } = this.refs;
    const hotspotsContainer = this.parentElement;
    if (!hotspotsContainer) return;

    const BUTTON_GAP = 10;
    const CONTAINER_GAP = 10;
    const TOTAL_GAP = BUTTON_GAP + CONTAINER_GAP;
    const containerRect = hotspotsContainer.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();

    this.#isMeasuring = true;
    dialog.style.visibility = 'hidden';
    dialog.style.display = 'block';
    dialog.style.transform = 'none';
    dialog.removeAttribute('data-placement');
    const { width: dialogWidth, height: dialogHeight } = dialog.getBoundingClientRect();
    dialog.style.removeProperty('display');
    dialog.style.removeProperty('visibility');
    dialog.style.removeProperty('transform');
    this.#isMeasuring = false;

    const buttonLeft = triggerRect.left - containerRect.left;
    const buttonRight = triggerRect.right - containerRect.left;
    const buttonTop = triggerRect.top - containerRect.top;
    const buttonBottom = triggerRect.bottom - containerRect.top;
    const spaceRight = containerRect.width - buttonRight - CONTAINER_GAP;
    const spaceLeft = buttonLeft - CONTAINER_GAP;

    let x = 'right';
    if (spaceRight >= dialogWidth + BUTTON_GAP) {
      x = 'right';
    } else if (spaceLeft >= dialogWidth + BUTTON_GAP) {
      x = 'left';
    } else {
      x = 'center';
    }

    let y = 'bottom';
    let verticalOffset = 0;
    if (x !== 'center') {
      let dialogStartY = buttonTop;
      let dialogEndY = buttonTop + dialogHeight;
      if (dialogEndY > containerRect.height - CONTAINER_GAP) {
        dialogStartY = buttonBottom - dialogHeight;
        dialogEndY = buttonBottom;
        y = 'top';
        if (dialogStartY < CONTAINER_GAP) {
          verticalOffset = CONTAINER_GAP - dialogStartY;
        } else if (dialogEndY > containerRect.height - CONTAINER_GAP) {
          verticalOffset = -(dialogEndY - (containerRect.height - CONTAINER_GAP));
        }
      } else {
        if (dialogStartY < CONTAINER_GAP) {
          verticalOffset = CONTAINER_GAP - dialogStartY;
          y = 'bottom';
        }
      }
    } else {
      if (containerRect.height - buttonBottom >= dialogHeight + TOTAL_GAP) {
        y = 'bottom';
      } else if (buttonTop >= dialogHeight + TOTAL_GAP) {
        y = 'top';
      } else {
        y = buttonTop < containerRect.height / 2 ? 'bottom' : 'top';
      }
    }

    dialog.dataset.placement = `${x},${y}`;
    if (verticalOffset !== 0) {
      dialog.style.setProperty('--dialog-vertical-offset', `${verticalOffset}px`);
    } else {
      dialog.style.removeProperty('--dialog-vertical-offset');
    }
    return new Promise((resolve) => setTimeout(resolve, 100));
  }

  #handlePointerLeave = (e) => {
    if (this.#isMeasuring) return;
    if (e.pointerType === 'touch') return;

    const { dialog, trigger } = this.refs;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (!dialog.open) return;

    const relatedTarget = e.relatedTarget;
    const isGoingInsideDialog =
      relatedTarget instanceof Element &&
      (relatedTarget === dialog || dialog.contains(relatedTarget));
    const isGoingToTrigger =
      relatedTarget instanceof Element &&
      (relatedTarget === trigger || trigger.contains(relatedTarget));

    if (isGoingInsideDialog || isGoingToTrigger) return;
    console.log('[hotspot] closeDialog from #handlePointerLeave');
    this.closeDialog();
  };

  getHotspotProductLink() {
    return this.refs.productLink || null;
  }

  handleHotspotClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('[hotspot] handleHotspotClick', Date.now());
    this.showDialog();
  };

  showDialog = async () => {
    console.log('[hotspot] showDialog start', Date.now());
    const { dialog } = this.refs;
    await this.#calculateDialogPlacement();
    dialog.dataset.showing = 'true';
    dialog.show();
    this.#openedAt = Date.now();
    console.log('[hotspot] dialog shown, open=', dialog.open, 'openedAt=', this.#openedAt);
    setTimeout(() => {
      console.log('[hotspot] registering listeners');
      document.body.addEventListener('click', this.lightDismissMouse);
      document.body.addEventListener('keydown', this.lightDismissKeyboard);
      document.body.addEventListener('keyup', this.lightDismissKeyboard);
      dialog.addEventListener('pointerleave', this.#handlePointerLeave);
    }, 100);
  };

  closeDialog = async () => {
    console.log('[hotspot] closeDialog called from:', new Error().stack);
    const { dialog, trigger } = this.refs;
    dialog.dataset.closing = 'true';
    dialog.close();
    document.body.removeEventListener('click', this.lightDismissMouse);
    document.body.removeEventListener('keydown', this.lightDismissKeyboard);
    document.body.removeEventListener('keyup', this.lightDismissKeyboard);
    dialog.removeEventListener('pointerleave', this.#handlePointerLeave);
    trigger.removeEventListener('pointerleave', this.#handlePointerLeave);
    const animations = dialog.getAnimations({ subtree: true });
    await Promise.allSettled(animations.map((a) => a.finished));
    if (!dialog.open) {
      delete dialog.dataset.showing;
      delete dialog.dataset.closing;
      delete dialog.dataset.placement;
    }
  };

  lightDismissMouse = (event) => {
    const { dialog } = this.refs;
    console.log('[hotspot] lightDismissMouse, dialog.open=', dialog.open, 'diff=', Date.now() - this.#openedAt);
    if (!dialog.open) return;
    if (Date.now() - this.#openedAt < 300) return;
    if (isClickedOutside(event, dialog)) {
      console.log('[hotspot] closeDialog from lightDismissMouse');
      this.closeDialog();
    }
  };

  lightDismissKeyboard = (event) => {
    const { dialog } = this.refs;
    console.log('[hotspot] lightDismissKeyboard, type=', event.type, 'key=', event.key);
    if (
      (event.type === 'keydown' && event.key === 'Escape') ||
      (event.type === 'keyup' && !dialog.matches(':is(:focus, :focus-visible, :focus-within)'))
    ) {
      console.log('[hotspot] closeDialog from lightDismissKeyboard');
      this.closeDialog();
    }
  };
}

customElements.define('product-hotspot-component', ProductHotspotComponent);
